import { Link, type LinkOptions } from '@tanstack/react-router'
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import type { ReactNode } from 'react'
import { Sparkline } from './charts'
import { cx } from './cx'

export type Delta = {
  /** Display text, already formatted ("+3", "−4 %"). */
  value: string
  /** Whether the change is good, bad or neither — not its sign (fewer goals conceded is positive). */
  tone: 'positive' | 'negative' | 'neutral'
  direction?: 'up' | 'down' | 'flat'
  /** Comparison basis, e.g. "zum Vormonat". */
  label?: string
}

type StatTileProps = {
  label: ReactNode
  value: ReactNode
  unit?: ReactNode
  delta?: Delta
  /** Small trend line; last value = current. */
  trend?: number[]
  /** Secondary line under the value ("Platz 3 von 16"). */
  meta?: ReactNode
  link?: LinkOptions
}

const deltaTone = { positive: 'text-positive', negative: 'text-negative', neutral: 'text-ink-3' }
const deltaIcon = { up: ArrowUpRight, down: ArrowDownRight, flat: Minus }

/** A key figure. Only show figures that carry a decision. */
export function StatTile({ label, value, unit, delta, trend, meta, link }: StatTileProps) {
  const DeltaIcon = delta ? deltaIcon[delta.direction ?? 'flat'] : null
  const body = (
    <>
      <span className="text-small text-ink-2">{label}</span>
      <span className="mt-2 flex items-end justify-between gap-3">
        <span className="flex items-baseline gap-1">
          <span className="text-display font-medium text-ink tabular">{value}</span>
          {unit && <span className="text-body text-ink-3">{unit}</span>}
        </span>
        {/* Phones: two tiles per row leave no room — the trend is supplementary, the value is not. */}
        {trend && trend.length > 1 && (
          <span className="mb-1.5 hidden sm:block">
            <Sparkline values={trend} className="h-7 w-20 text-accent" />
          </span>
        )}
      </span>
      {(delta || meta) && (
        <span className="mt-1.5 flex flex-col gap-0.5 text-small">
          {delta && DeltaIcon && (
            // Inline flow (not flex) so a long comparison label wraps like text in narrow tiles.
            <span>
              <span className={cx('font-medium whitespace-nowrap tabular', deltaTone[delta.tone])}>
                <DeltaIcon aria-hidden="true" className="-mt-0.5 mr-0.5 inline size-3.5" strokeWidth={2.25} />
                {delta.value}
              </span>
              {delta.label && <span className="text-ink-3"> {delta.label}</span>}
            </span>
          )}
          {meta && <span className="text-ink-3">{meta}</span>}
        </span>
      )}
    </>
  )
  const cls = 'flex min-w-0 flex-col rounded-card border border-line bg-surface p-4 md:p-5'
  if (!link) return <div className={cls}>{body}</div>
  return (
    <Link
      {...link}
      className={cx(cls, 'transition-[background-color,border-color] duration-150 hover:border-line-strong hover:bg-bg-subtle')}
    >
      {body}
    </Link>
  )
}

/** Responsive row of StatTiles: 2 columns on phones, up to 4 on desktop. */
export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">{children}</div>
}
